import { FC, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { routesPath } from '@router/routes';
import { replacePathParams } from '@utils/replacePathWithParams';
import { prepareUrl } from 'src/utils/routerHelpers';
import { useQCTReadingsSlice } from '@restore/QCTReadings/QCTReadings.slice';
import { useQCTaskSlice } from '@restore/QCTask/QCTask.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import {
  filterQCTLabels,
  selectQCTLabels,
  selectQCTVisitLabels,
  toVisitReadings,
} from 'src/utils/qctHelpers';
import { WorkstationPageHeader } from '../components/WorkstationPageHeader';
import { ReadingsTable } from './ReadingsTable';
import { WorkstationResultsTable } from './WorkstationResultsTable';

type QCTPageViewProps = {
  loading?: boolean;
  clinicalTrialId: string;
  taskId: string;
};

export const QCTPageView: FC<QCTPageViewProps> = ({
  loading,
  clinicalTrialId,
  taskId,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [
    {
      lastSync,
      loadingReadings,
      loadingWorkstationReadings,
      syncStatus,
      visitData,
      workstationData,
    },
    {
      syncWorkstation,
      fetchQCTReadings,
      fetchQCTWorkstationReadings,
      getSyncStatus,
      stopSyncPolling,
      sendToReadings,
      removeSelectedReading,
      changeReadingLabel,
    },
  ] = useQCTReadingsSlice((state, actions) => [state, actions]);

  const [assigneeId, taskStatus, visitDefinitionId, taskDataIsLoading] =
    useQCTaskSlice(state => [
      state.data?.assigneeId,
      state.data?.status,
      state.data?.visitDefinitionId,
      state.loading,
    ]);

  const studyLabels = useClinicalTrialContextSlice(s =>
    selectQCTLabels(s.groupedLabels)
  );

  const flatVisits = useClinicalTrialContextSlice(s => s.flatVisits);

  const groupedLabels = useMemo(
    () => selectQCTVisitLabels(flatVisits, visitDefinitionId) ?? studyLabels,
    [flatVisits, visitDefinitionId, studyLabels]
  );

  const availableLabels = useMemo(
    () => filterQCTLabels(groupedLabels, visitData),
    [groupedLabels, visitData]
  );

  const selectedReadings = useMemo(
    () => toVisitReadings(visitData, groupedLabels) ?? [],
    [visitData, groupedLabels]
  );

  const goToTaskInfo = () =>
    navigate(prepareUrl(routesPath.qcTaskInfo, [clinicalTrialId, taskId]));

  useEffect(() => {
    fetchQCTReadings({ clinicalTrialId, taskId });
    fetchQCTWorkstationReadings({ clinicalTrialId, taskId });
    getSyncStatus({ clinicalTrialId, taskId });

    // The poller runs every 30s until the query settles; without this it keeps
    // going after the specialist has left, and would toast over another page.
    return () => {
      stopSyncPolling(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, taskId]);

  useEffect(() => {
    return () => {
      sessionStorage.setItem('previousLocation', pathname);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTaskClosed = taskStatus === 'DONE' || taskStatus === 'FAILED';

  const canEdit = !!assigneeId && !isTaskClosed;

  const anyLoading =
    loading ||
    loadingWorkstationReadings ||
    loadingReadings ||
    taskDataIsLoading;

  return (
    <div data-testid='QCTTask' className={clsx('min-h-[calc(100%-3.6rem)] pb-10')}>
      <div className='flex items-center justify-between py-4'>
        <WorkstationPageHeader
          title='QCT QC'
          goToTaskInfo={goToTaskInfo}
          assigneeExist={canEdit}
          loading={anyLoading}
        />
        {canEdit && (
          <Button
            className='min-w-[12rem] rounded-lg ml-1 !px-2'
            type='primary'
            data-testid='QCTSaveAndClose'
            onClick={() =>
              navigate(
                replacePathParams(routesPath.qcTaskInfo, {
                  id: clinicalTrialId,
                  taskId,
                })
              )
            }
          >
            Save &amp; Close
          </Button>
        )}
      </div>

      <div
        data-testid='QCTWorkstationResults'
        className='overflow-auto h-[calc(100vh-250px)] px-2'
      >
        <ReadingsTable
          readings={selectedReadings}
          labels={availableLabels}
          onLabelChange={(data, labelId) =>
            changeReadingLabel({ clinicalTrialId, taskId, data, labelId })
          }
          onRemove={data =>
            removeSelectedReading({ clinicalTrialId, taskId, data })
          }
          loading={loading || loadingReadings || taskDataIsLoading}
          readOnly={!canEdit}
        />

        <WorkstationResultsTable
          labels={availableLabels}
          sentForReading={selectedReadings}
          syncStatus={syncStatus}
          sendToReadings={data =>
            sendToReadings({ clinicalTrialId, taskId, data })
          }
          onSync={() => syncWorkstation({ clinicalTrialId, taskId })}
          lastSync={lastSync}
          workstationData={workstationData}
          loading={anyLoading}
          readOnly={!canEdit}
        />
      </div>
    </div>
  );
};
